<template>
  <div class="order-container">
    <el-header>
      <div class="header-content">
        <el-button 
          icon="el-icon-back" 
          @click="goBack" 
          class="back-button"
        >
          Back
        </el-button>
        <h1>Order Details</h1>
        <div class="header-spacer"></div>
      </div>
    </el-header>
    <el-main>
      <el-card class="order-card">
        <div class="collection-preview">
          <div class="cover-image">
            <img v-if="collection.cover_url" :src="collection.cover_url" alt="Collection Cover">
            <div v-else class="placeholder-cover">
              <i class="el-icon-picture-outline"></i>
            </div>
          </div>
          <div class="collection-info">
            <h2>{{ collection.title }}</h2>
            <p class="author">By {{ collection.user_name }}</p>
            <div class="collection-stats">
              <span><i class="el-icon-folder"></i> {{ collection.group_count || 0 }} Groups</span>
              <span><i class="el-icon-document"></i> {{ collection.knowit_count || collection.planned_knowit_count || 0 }} Knowits</span>
              <span><i class="el-icon-reading"></i> {{ collection.content_count || 0 }} Contents</span>
            </div>
          </div>
        </div>

        <div class="order-details">
          <div class="price-details">
            <div class="price-row">
              <h3>Price</h3>
              <div class="price-amount">
                <span v-if="isPreorderMode && collectionOriginalPrice > collectionPrice" class="original-price">
                  {{ formatMoney(collectionOriginalPrice) }}
                </span>
                <span>{{ collectionPriceLabel }}</span>
              </div>
            </div>
            <div v-if="isPreorderMode" class="preorder-note">
              <el-tag size="mini" type="warning">Pre-order</el-tag>
              <span>
                Reserve at 50% off. Delivery is targeted within 14 days after the first pre-order, and reading unlocks when the course is published.
              </span>
            </div>
            <div class="price-row total-row">
              <h3>Total</h3>
              <div class="price-amount total-price">
                <span>{{ collectionPriceLabel }}</span>
              </div>
            </div>
          </div>

          <div class="order-summary">
            <h3>Order Summary</h3>
            <div class="summary-item">
              <span>Order Time</span>
              <span>{{ formatDate(new Date()) }}</span>
            </div>
            <div class="summary-item">
              <span>Order Status</span>
              <el-tag size="small" :type="isPreorderMode ? 'info' : 'warning'">
                {{ isPreorderMode ? 'Pre-order' : 'Pending Payment' }}
              </el-tag>
            </div>
          </div>

          <div class="payment-section">
            <el-button 
              type="primary" 
              :loading="creating" 
              @click="createOrder"
              style="width: 100%"
            >
              {{ orderButtonLabel }}
            </el-button>
            <el-button
              v-if="!isPreorderMode && availableCollectionVoucher"
              :loading="redeemingVoucher"
              @click="confirmVoucherRedemption(availableCollectionVoucher)"
              style="width: 100%; margin: 10px 0 0 0"
            >
              Use voucher instead
            </el-button>
            <p class="payment-note">
              <span v-if="isPreorderMode">Pre-orders are demand signals for production priority. The course will become readable after it is delivered and published.</span>
              <span v-else>By proceeding, you agree to our <a href="#">User Policy</a> and <a href="#">Terms of Service</a>.</span>
            </p>
          </div>
        </div>
      </el-card>
    </el-main>
  </div>
</template>

<script>
import { listMyVouchers, postRequest, redeemCollectionVoucher } from '@/apis/front'

export default {
  name: 'CollectionOrder',
  data() {
    return {
      collection: {},
      creating: false,
      redeemingVoucher: false,
      vouchers: []
    }
  },
  computed: {
    isPreorderMode() {
      return this.isPreorderStatus(this.collection.status)
    },
    hasActivePlatformSubscription() {
      const platformSubscription = this.$store.state.platformSubscription || {}
      return Boolean(platformSubscription.active || this.collection.has_platform_subscription)
    },
    collectionOriginalPrice() {
      return this.getEffectiveCollectionPrice(this.collection)
    },
    collectionPrice() {
      if (this.isPreorderMode) {
        return this.hasActivePlatformSubscription ? 0 : this.getPreorderCollectionPrice(this.collection)
      }
      return this.collectionOriginalPrice
    },
    collectionPriceLabel() {
      if (this.isPreorderMode && this.hasActivePlatformSubscription) {
        return 'Included with plan'
      }
      return this.formatMoney(this.collectionPrice)
    },
    orderButtonLabel() {
      if (this.isPreorderMode) {
        return this.hasActivePlatformSubscription ? 'Pre-order with Plan' : 'Confirm Pre-order'
      }
      return this.collectionPrice > 0 ? 'Proceed to Payment' : 'Confirm and Claim Free'
    },
    availableCollectionVoucher() {
      return this.vouchers.find(voucher => voucher.voucher_type === 'collection_purchase' && ['issued', 'claimed'].includes(voucher.status))
    }
  },
  created() {
    this.fetchCollectionData()
    this.fetchVouchers()
    if (!this.$store.state.platformSubscription && this.$store.state.user_id) {
      this.$store.dispatch('fetchPlatformSubscription')
    }
  },
  beforeDestroy() {
    // Clean up the temporary collection ID from store
    this.$store.dispatch('setTempCollectionId', null)
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleString()
    },
    getEffectiveCollectionPrice(collection) {
      if (!collection) return 0
      const rawPrice = collection.has_custom_price === true
        ? collection.usd_price
        : collection.base_credits
      const price = Number(rawPrice)
      return Number.isFinite(price) && price > 0 ? Math.round(price * 100) / 100 : 0
    },
    getPreorderCollectionPrice(collection) {
      if (!collection) return 0
      const backendPrice = Number(collection.preorder_price)
      if (Number.isFinite(backendPrice) && backendPrice > 0) {
        return Math.round(backendPrice * 100) / 100
      }
      const price = this.getEffectiveCollectionPrice(collection)
      return price > 0 ? Math.round(price * 50) / 100 : 0
    },
    isPreorderStatus(status) {
      return ['PREORDER', 'TEASER', 'REQUEST'].includes(status)
    },
    formatMoney(value) {
      const amount = Number(value)
      return amount > 0 ? `$${amount.toFixed(2)}` : 'FREE'
    },
    async fetchCollectionData() {
      try {
        const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id
        
        if (!collectionId) {
          this.$message.error('Collection ID not found')
          this.$router.go(-1)
          return
        }
        
        const response = await postRequest('getPostCollection', {
          id: collectionId,
          user_id: this.$store.state.user_id
        })
        
        if (response.data.code === 0) {
          this.collection = response.data.data
        } else {
          this.$message.error(response.data.msg || 'Failed to load collection')
        }
      } catch (error) {
        this.$message.error('Failed to load collection')
      }
    },
    async fetchVouchers() {
      if (!this.$store.state.user_id) return
      try {
        const response = await listMyVouchers({
          user_id: this.$store.state.user_id,
          voucher_type: 'collection_purchase'
        })
        if (response.data.code === 0) {
          this.vouchers = (response.data.data && response.data.data.items) || []
        }
      } catch (error) {
        this.vouchers = []
      }
    },
    async createOrder() {
      if (!this.$store.state.user_id) {
        this.$router.push('/login')
        return
      }

      if (!this.isPreorderMode && this.availableCollectionVoucher) {
        this.$confirm(
          'You have a collection purchase voucher. Use it for this collection?',
          'Use voucher',
          {
            confirmButtonText: 'Use voucher',
            cancelButtonText: 'Pay normally',
            type: 'info'
          }
        ).then(() => {
          this.confirmVoucherRedemption(this.availableCollectionVoucher)
        }).catch(() => {
          this.createPaidOrder()
        })
        return
      }

      this.createPaidOrder()
    },
    async createPaidOrder() {
      if (!this.$store.state.user_id) {
        this.$router.push('/login')
        return
      }

      this.creating = true
      try {
        const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id
        const response = await postRequest('createCollectionOrder', {
          user_id: this.$store.state.user_id,
          collection_id: collectionId,
          status: this.isPreorderMode
            ? (this.hasActivePlatformSubscription ? 'SUBSCRIPTION_PREORDER' : 'PREORDER')
            : undefined,
          usd_price: this.collectionPrice
        })
        const orderData = response.data.data || {}
        
        if (response.data.code === 0 && orderData.status === 'PENDING') {
          this.$message.success('Order created successfully')
          if (this.collectionPrice > 0) {
            this.$router.push({
              path:'/pay',
              query:{
                order_id: this.extractId(orderData),
                order_type:"collection"
              }
            })
          } else {
            this.$store.dispatch('setTempCollectionId', collectionId)
            this.$router.push({ path: '/read-collection', query: { collection_id: collectionId } })
          }
        } else if (response.data.code === 0 && ['PREORDER', 'SUBSCRIPTION_PREORDER'].includes(orderData.status)) {
          this.$message.success('Pre-order received')
          this.$store.dispatch('setTempCollectionId', collectionId)
          this.$router.push({ path: '/deck', query: { id: collectionId } })
        } else if (response.data.code === -2 || (response.data.code === 0 && orderData.status === 'COMPLETE')) {
          this.$message.warning(this.isPreorderMode ? 'You have already pre-ordered this course' : 'You have already purchased this course')
          this.$store.dispatch('setTempCollectionId', collectionId)
          this.$router.push({ path: '/deck', query: { id: collectionId } })
        } else {
          throw new Error(response.data.msg || 'Failed to create order')
        }
      } catch (error) {
        this.$message.error(error.message || 'Failed to create order')
      } finally {
        this.creating = false
      }
    },
    async confirmVoucherRedemption(voucher) {
      if (!voucher || this.redeemingVoucher) return
      const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id
      const voucherId = this.extractId(voucher)
      if (!collectionId || !voucherId) return

      this.redeemingVoucher = true
      try {
        const response = await redeemCollectionVoucher({
          user_id: this.$store.state.user_id,
          voucher_id: voucherId,
          collection_id: collectionId
        })
        if (response.data.code === 0) {
          this.$message.success('Voucher redeemed')
          this.$store.dispatch('setTempCollectionId', collectionId)
          this.$router.push({ path: '/read-collection', query: { collection_id: collectionId } })
        } else {
          this.$message.error(response.data.msg || 'Failed to redeem voucher')
        }
      } catch (error) {
        this.$message.error('Failed to redeem voucher')
      } finally {
        this.redeemingVoucher = false
      }
    },
    extractId(obj) {
      if (!obj) return ''
      if (typeof obj === 'string') return obj
      if (obj.id) return obj.id
      const id = obj._id
      if (!id) return ''
      if (typeof id === 'string') return id
      return id.$oid || id.$id || ''
    },
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.order-container {
  width: 100%;
}

.el-header {
  background-color: #409EFF;
  color: white;
  padding: 0;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.header-content {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  justify-content: space-between;
  height: 100%;
}

.order-card {
  width: 70%;
  margin: 20px auto;
  padding: 30px;
}

.collection-preview {
  display: flex;
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #EBEEF5;
}

.cover-image {
  width: 150px;
  height: 212px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f7fa;
}

.cover-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.collection-info {
  flex: 1;
}

.collection-info h2 {
  margin: 0 0 10px 0;
}

.author {
  color: #606266;
  margin-bottom: 15px;
}

.collection-stats {
  display: flex;
  gap: 20px;
}

.collection-stats span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #606266;
}

.order-details {
  padding-top: 20px;
}

.price-details {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #fafafa;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.price-row:last-child {
  border-bottom: none;
}

.price-row h3 {
  margin: 0;
  font-size: 16px;
  color: #616161;
}

.price-amount {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  color: #212121;
}

.original-price {
  color: #909399;
  font-size: 14px;
  font-weight: 400;
  text-decoration: line-through;
}

.preorder-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 0 12px;
  color: #606266;
  font-size: 13px;
  line-height: 1.45;
}

.total-row h3,
.total-row .total-price {
  font-weight: 700;
  font-size: 18px;
  color: #212121;
}

.order-summary {
  margin-bottom: 30px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #EBEEF5;
}

.payment-section {
  text-align: center;
}

.payment-note {
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
}

.back-button {
  position: absolute;
  left: 20px;
  color: white;
  border: 1px solid white;
  background: transparent;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-color: white;
}

.header-content h1 {
  flex: 1;
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
  margin: 0;
  color: white;
  line-height: 72px;
}

.payment-methods {
  margin-top: 20px;
}
</style>
